import React, { useState } from "react";
import axios from "axios";
import {
  MapPin,
  Phone,
  Mail,
  Facebook,
  Instagram,
  Send,
  ExternalLink,
  Navigation,
  Loader2,
} from "lucide-react";
import Icon from "./Icon";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./ui/accordion";
import { useToast } from "../hooks/use-toast";
import { contact } from "../mock";
import { useLang, heroImages } from "../i18n";
import { WhatsAppButton, WhatsAppIcon, waLink } from "./WhatsApp";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function Guending() {
  const { t } = useLang();
  const g = t.guending;
  return (
    <section id="guending" className="bg-white py-20 lg:py-28">
      <div className="mx-auto max-w-7xl px-6">
        <div className="grid gap-16 lg:grid-cols-12 lg:gap-20">
          <div className="lg:col-span-7">
            <p data-reveal className="eyebrow text-[#55575e]">
              {g.eyebrow}
            </p>
            <h2
              data-reveal
              data-delay="1"
              className="mt-4 font-display text-4xl leading-tight text-[#2a2b2f] sm:text-5xl"
            >
              {g.title}
            </h2>
            <div data-reveal data-delay="2" className="mt-7 space-y-5">
              {g.paragraphs.map((p) => (
                <p key={p.slice(0, 24)} className="text-[16px] leading-[1.8] text-slate-600">
                  {p}
                </p>
              ))}
            </div>

            <div data-reveal data-delay="3" className="mt-10 grid gap-4 sm:grid-cols-4">
              {g.facts.map((f) => (
                <div key={f.label} className="rounded-xl border border-slate-200 bg-[#fafafb] px-4 py-4">
                  <p className="text-[11px] uppercase tracking-wider text-slate-500">{f.label}</p>
                  <p className="mt-1 font-display text-xl text-[#2a2b2f]">{f.value}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5">
            <div
              data-reveal
              data-delay="2"
              className="img-zoom overflow-hidden rounded-[1.5rem] border border-slate-200 shadow-[0_30px_70px_-40px_rgba(42,43,47,0.5)]"
            >
              <img
                src="/images/amper.jpg"
                alt={g.amperAlt}
                loading="eager"
                decoding="async"
                className="pixel-soft h-64 w-full object-cover"
              />
            </div>

            <div data-reveal data-delay="3" className="mt-6 rounded-[1.5rem] border border-slate-200 bg-white p-7">
              <h3 className="text-[17px] font-semibold text-[#2a2b2f]">{g.distancesTitle}</h3>
              <ul className="mt-5 divide-y divide-slate-100">
                {g.distances.map((d) => (
                  <li key={d.place} className="flex items-center justify-between gap-4 py-3">
                    <span className="flex items-center gap-3 text-[14.5px] text-slate-600">
                      <Icon name={d.icon} className="h-4 w-4 text-[#55575e]" />
                      {d.place}
                    </span>
                    <span className="text-[14px] font-semibold text-[#2a2b2f]">{d.km}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export function Faq() {
  const { t } = useLang();
  const s = t.faqSection;
  const img = t.gallery.find((g) => g.src === heroImages.faq) || t.gallery[0];
  return (
    <section className="surface-soft py-20 lg:py-28">
      <div className="mx-auto max-w-7xl px-6">
        <div className="grid gap-14 lg:grid-cols-12 lg:gap-20">
          <div className="lg:col-span-4">
            <p data-reveal className="eyebrow text-[#55575e]">
              {s.eyebrow}
            </p>
            <h2
              data-reveal
              data-delay="1"
              className="mt-4 font-display text-4xl leading-tight text-[#2a2b2f] sm:text-[2.9rem]"
            >
              {s.title}
            </h2>
            <p data-reveal data-delay="2" className="mt-5 text-[16px] leading-relaxed text-slate-600">
              {s.lead}
            </p>
            <div
              data-reveal
              data-delay="3"
              className="mt-8 img-zoom overflow-hidden rounded-2xl border border-slate-200"
            >
              <img
                src={img.src}
                alt={img.alt}
                loading="eager"
                decoding="async"
                className="pixel-soft h-48 w-full object-cover"
              />
            </div>
          </div>

          <div data-reveal data-delay="2" className="lg:col-span-8">
            <Accordion type="single" collapsible className="space-y-4">
              {t.faqs.map((f, i) => (
                <AccordionItem
                  key={f.q}
                  value={`item-${i}`}
                  className="rounded-2xl border border-slate-200 bg-white px-6 data-[state=open]:border-[#b6b8be]"
                >
                  <AccordionTrigger className="py-5 text-left text-[16px] font-semibold text-[#2a2b2f] hover:no-underline">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="pb-5 text-[15px] leading-relaxed text-slate-600">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </div>
    </section>
  );
}

export function Contact() {
  const { t, lang } = useLang();
  const c = t.contactSection;
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast({ title: c.errTitle, description: c.errText });
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/inquiries`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        message: form.message.trim(),
        lang,
      });
      toast({ title: c.okTitle, description: c.okText });
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast({
        title: c.failTitle,
        description: typeof detail === "string" ? detail : c.failText,
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="kontakt" className="bg-white py-20 lg:py-28">
      <div className="mx-auto max-w-7xl px-6">
        <div className="max-w-2xl">
          <p data-reveal className="eyebrow text-[#55575e]">
            {c.eyebrow}
          </p>
          <h2
            data-reveal
            data-delay="1"
            className="mt-4 font-display text-4xl leading-tight text-[#2a2b2f] sm:text-5xl"
          >
            {c.title}
          </h2>
          <p data-reveal data-delay="2" className="mt-5 text-[16px] leading-relaxed text-slate-600">
            {c.lead}
          </p>
        </div>

        <div className="mt-14 grid gap-8 lg:grid-cols-12">
          <div data-reveal className="lg:col-span-5 space-y-5">
            <div className="rounded-[1.5rem] border border-slate-200 bg-[#f5f6f8] p-7">
              <h3 className="font-display text-2xl text-[#2a2b2f]">{c.addressTitle}</h3>
              <div className="mt-5 space-y-4 text-[15px] text-slate-600">
                <p className="flex gap-3">
                  <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-[#55575e]" />
                  <span>
                    {contact.company}
                    <br />
                    {contact.street}
                    <br />
                    {contact.zipCity}
                  </span>
                </p>
                <p className="flex gap-3">
                  <Phone className="mt-0.5 h-4 w-4 shrink-0 text-[#55575e]" />
                  <a href={`tel:${contact.phoneLink}`} className="hover:text-[#2a2b2f] transition-colors">
                    {contact.phoneDisplay}
                  </a>
                </p>
                <p className="flex gap-3">
                  <Mail className="mt-0.5 h-4 w-4 shrink-0 text-[#55575e]" />
                  <a
                    href={`mailto:${contact.email}`}
                    className="break-all hover:text-[#2a2b2f] transition-colors"
                  >
                    {contact.email}
                  </a>
                </p>
              </div>

              <div className="mt-7">
                <WhatsAppButton full />
                <p className="mt-3 text-[13px] leading-relaxed text-slate-500">{t.whatsapp.note}</p>
              </div>

              <div className="mt-6 flex flex-wrap gap-3">
                <a
                  href={contact.facebook}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-5 py-2.5 text-[13.5px] font-medium text-[#2a2b2f] transition-all duration-300 hover:-translate-y-0.5 hover:border-[#2a2b2f]"
                >
                  <Facebook className="h-4 w-4" /> Facebook
                </a>
                <a
                  href={contact.instagram}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-5 py-2.5 text-[13.5px] font-medium text-[#2a2b2f] transition-all duration-300 hover:-translate-y-0.5 hover:border-[#2a2b2f]"
                >
                  <Instagram className="h-4 w-4" /> Instagram
                </a>
              </div>
            </div>

            <div className="overflow-hidden rounded-[1.5rem] border border-slate-200">
              <div className="relative">
                <iframe
                  title={c.mapTitle}
                  src={contact.mapsEmbed}
                  className="block h-[260px] w-full border-0"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
                <div className="pointer-events-none absolute left-3 top-3 rounded-xl bg-white/95 px-4 py-2.5 shadow-[0_10px_26px_-12px_rgba(0,0,0,0.45)] ring-1 ring-black/5 backdrop-blur-sm">
                  <span className="flex items-center gap-2 text-[14px] font-bold text-[#d7263d]">
                    <MapPin className="h-4 w-4 shrink-0 text-[#d7263d]" />
                    {contact.street}
                  </span>
                  <span className="mt-0.5 block pl-6 text-[12.5px] text-slate-500">
                    {contact.zipCity}
                  </span>
                </div>
              </div>
              <a
                href={contact.mapsLink}
                target="_blank"
                rel="noreferrer"
                className="flex items-center justify-between gap-3 bg-white px-6 py-4 text-[14px] font-medium text-[#2a2b2f] transition-colors hover:bg-[#f5f6f8]"
              >
                <span className="inline-flex items-center gap-2">
                  <Navigation className="h-4 w-4" /> {c.route}
                </span>
                <ExternalLink className="h-4 w-4" />
              </a>
            </div>
          </div>

          <div data-reveal data-delay="2" className="lg:col-span-7">
            <form
              onSubmit={submit}
              className="rounded-[1.5rem] border border-slate-200 bg-white p-7 shadow-[0_30px_70px_-50px_rgba(42,43,47,0.6)] sm:p-9"
            >
              <h3 className="font-display text-2xl text-[#2a2b2f]">{c.formTitle}</h3>
              <p className="mt-2 text-[14px] text-slate-500">{c.formNote}</p>

              <div className="mt-7 grid gap-5 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="c-name" className="text-[13px] font-medium text-slate-700">
                    {c.name} *
                  </Label>
                  <Input
                    id="c-name"
                    value={form.name}
                    onChange={update("name")}
                    placeholder={c.namePh}
                    className="h-11 rounded-xl border-slate-200"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="c-email" className="text-[13px] font-medium text-slate-700">
                    {c.email} *
                  </Label>
                  <Input
                    id="c-email"
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    placeholder={c.emailPh}
                    className="h-11 rounded-xl border-slate-200"
                  />
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="c-phone" className="text-[13px] font-medium text-slate-700">
                    {c.phone}
                  </Label>
                  <Input
                    id="c-phone"
                    value={form.phone}
                    onChange={update("phone")}
                    placeholder={c.phonePh}
                    className="h-11 rounded-xl border-slate-200"
                  />
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="c-msg" className="text-[13px] font-medium text-slate-700">
                    {c.message} *
                  </Label>
                  <Textarea
                    id="c-msg"
                    value={form.message}
                    onChange={update("message")}
                    rows={5}
                    placeholder={c.messagePh}
                    className="rounded-xl border-slate-200"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={sending}
                className="btn-primary mt-7 inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-70"
              >
                {sending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> {c.sending}
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4" /> {c.submit}
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
